import React from "react";
import Modal from "react-modal";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "../Button";
import { Label } from "../Label";
import { ReactComponent as CloseIcon } from "../../assets/icons/x.svg";

const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6d4c91] focus:border-transparent font-mono text-xs";

interface LinkRowProps {
  label: string;
  value: string;
  hint: string;
  /** True once the token has lapsed: the link still reads fine but would 401 on a phone. */
  disabled: boolean;
  onCopy: () => void;
}

const LinkRow: React.FC<LinkRowProps> = ({ label, value, hint, disabled, onCopy }) => (
  <div>
    <Label>{label}</Label>
    <div className="flex gap-2 mt-1">
      <input readOnly value={value} onFocus={(e) => e.target.select()} className={inputClass} />
      <Button variant="outline" size="sm" type="button" disabled={disabled} onClick={onCopy}>
        Copy
      </Button>
      <Button
        variant="outline"
        size="sm"
        type="button"
        disabled={disabled}
        onClick={() => window.open(value, "_blank", "noopener,noreferrer")}
      >
        Open
      </Button>
    </div>
    <p className="text-xs text-gray-500 mt-1">{hint}</p>
  </div>
);

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** The web preview URL. Null while it is still being issued, or after a failure. */
  previewUrl: string | null;
  /** The Branch deep link built from the same token. Null when there is no token yet. */
  branchLink: string | null;
  expiresAt: unknown;
  /** The token has lapsed. Copy and Open are withheld; only Refresh remains. */
  stale: boolean;
  error: string | null;
  refreshing: boolean;
  onRefresh: () => void;
  onCopied: (message: string) => void;
}

export const PreviewModal: React.FC<Props> = ({
  isOpen, onClose, previewUrl, branchLink, expiresAt, stale, error, refreshing, onRefresh, onCopied,
}) => {
  const copy = (value: string, message: string) =>
    navigator.clipboard?.writeText(value).then(() => onCopied(message)).catch(() => {});

  // The token lasts 7 days, so a bare time would read as today and understate the link by a week.
  const expiryText = expiresAt
    ? `Valid until ${new Date(expiresAt as any).toLocaleString([], {
        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
      })}.`
    : "Valid for a limited time.";

  return (
    <Modal
      className="modal-content md:max-w-[560px] max-h-[90vh] overflow-y-auto"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Campaign preview"
    >
      <div className="flex justify-between items-start mb-4">
        <h2 className="text-primary font-medium text-xl">Campaign preview</h2>
        <Button onClick={onClose} variant="icon" type="button" aria-label="Close">
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      {error && <p className="text-xs text-red-600 mb-3">{error}</p>}
      {!previewUrl && !error && <p className="text-xs text-gray-500">Creating a preview link…</p>}

      {previewUrl && (
        <div className="space-y-4">
          {stale ? (
            <p className="text-xs text-red-600">
              This link has expired. Refresh it before opening or scanning.
            </p>
          ) : (
            <p className="text-xs text-gray-500">
              {expiryText} Open it on any phone; it shows the campaign regardless of status and
              records nothing.
            </p>
          )}

          <LinkRow
            label="Preview link"
            value={previewUrl}
            hint="Opens the web preview in a browser."
            disabled={stale}
            onCopy={() => copy(previewUrl, "Link copied")}
          />

          {branchLink && (
            <LinkRow
              label="App deep link"
              value={branchLink}
              hint="Opens the app if it is installed, otherwise the web preview."
              disabled={stale}
              onCopy={() => copy(branchLink, "Deep link copied")}
            />
          )}

          {branchLink && (
            <div className="flex flex-col items-center pt-2">
              <QRCodeSVG
                value={branchLink}
                // 288px, not 240: the link carries $canonical_url (required by the native app),
                // which puts it at QR version 12 / 65 modules. 288px keeps each module ~4px —
                // the same physical size the shorter version-9 link had at 240px.
                size={288}
                level="L"
                includeMargin
                className={`border border-gray-200 rounded bg-white ${stale ? "opacity-40" : ""}`}
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                Scan with a phone to open the deep link.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-3 mt-6">
        <Button variant="outline" type="button" onClick={onRefresh} isLoading={refreshing}>
          {previewUrl ? "Refresh link" : "Retry"}
        </Button>
        <Button type="button" onClick={onClose}>
          Done
        </Button>
      </div>
    </Modal>
  );
};
